import { useState } from 'react'
import { ErrorMessage, Form, Formik } from 'formik'
import { Dialog, DialogActions, DialogContent } from '@mui/material'
import { useTranslation } from 'react-i18next'
import type { AccountType } from '@dhl/mdp-api'
import { DialogTitleClosable } from '@dhl/components'
import { sortBy } from '@dhl/utils'
import { FormError } from '@dhl/error'
import { Box, Button, Checkbox, Divider, Grid, H5, H6, MenuItem, Stack } from '@dhl-ui/components'
import { SelectInput, TextFieldInput } from '@dhl/formik'
import { BusinessTypeEnum } from '~/api/constants'
import { type Product, type ShipmentOptionDto, useProducts } from '~/common/model/capabilities'
import {
  type Account,
  createAccountValidationSchema,
  getInitialSelectedAccountProducts,
  ProductDataTable,
  useAccountTypes,
} from '~/common/components/accountnumbers'

type Props = {
  isNew: boolean
  account: Account
  businessType?: string
  businessUnit: string
  shipmentOptions: ShipmentOptionDto[]
  onSubmit: (account: Account, allAccountProducts: Product[]) => void
  onCancel: () => void
}

const accountTypesWithProducts: AccountType[] = ['Parcel', 'GMB']
const accountTypesWithShipmentOptions: AccountType[] = ['Parcel']

export const AccountForm = ({
  isNew,
  account,
  businessType,
  businessUnit,
  shipmentOptions,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation()
  const isCCCOrg = (businessUnit as BusinessTypeEnum) === BusinessTypeEnum.CCC
  const accountTypes = useAccountTypes(account.organisationId)
  const validationSchema = createAccountValidationSchema(t, businessUnit)

  // if creating a new account and there's only one account type, default to it.
  // Otherwise, use the account's current type (editing) or let the user choose (new).
  const defaultAccountType = isNew && accountTypes.length === 1 ? accountTypes[0] : account.accountType

  const [selectedAccountType, setSelectedAccountType] = useState<AccountType | null>(null)

  const accountType = selectedAccountType ?? defaultAccountType

  const { products: allAccountProducts } = useProducts(businessUnit, account.countryCode, accountType)

  // only show products when account type = 'Parcel' or 'GMB'. Irrelevant for 'Verlader'.
  const showProducts = accountTypesWithProducts.includes(accountType)
  // only show shipment options when account type = 'Parcel' and business type = 'Organisation'. Irrelevant for 'Verlader'.
  const showShipmentOptions =
    accountTypesWithShipmentOptions.includes(accountType) && businessType === BusinessTypeEnum.ORGANISATION

  const isLoading = isNew && accountTypes.length === 0

  if (isLoading) return null

  return (
    <Formik<Account>
      initialValues={{ ...account, accountType }}
      onSubmit={(account) => onSubmit(account, allAccountProducts)}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, values: { selectedProducts, selectedShipmentOptions }, setFieldValue, handleSubmit }) => {
        const mapAccountTypeToMenuItem = (accountType: AccountType) => (
          <MenuItem
            disabled={!isNew}
            value={accountType}
            onClick={() => {
              setSelectedAccountType(accountType)
              const selectedProducts = getInitialSelectedAccountProducts(accountType, businessUnit, allAccountProducts)
              setFieldValue('selectedProducts', selectedProducts)
              setFieldValue('selectedParcelTypes', [])
              setFieldValue('selectedShipmentOptions', [])
            }}
            key={accountType}
          >
            {accountType}
          </MenuItem>
        )

        const toggleSelectedItem = (selectedItems: string[], key: string, fieldName: keyof Account) => {
          setFieldValue(
            fieldName,
            selectedItems.includes(key) ? selectedItems.filter((k) => k !== key) : [...selectedItems, key]
          )
        }

        return (
          <Form data-testid="add-account-form">
            <Dialog data-testid="account-form" open={true} fullWidth maxWidth="lg">
              <DialogTitleClosable onClose={onCancel}>
                <H5>
                  {isNew ? t('admin.accountNumbers.addAccountNumber') : t('admin.accountNumbers.updateAccountNumber')}
                </H5>
              </DialogTitleClosable>
              <Divider variant="fullWidth" sx={{ mb: 1 }} />
              <DialogContent sx={{ minHeight: 500 }}>
                <Grid container direction="column">
                  <Grid marginTop={1} marginBottom={2}>
                    <Grid container spacing={2}>
                      <Grid size={3}>
                        <SelectInput<AccountType>
                          name="accountType"
                          label="Account Type"
                          data-testid="account-type"
                          fullWidth
                          disabled={!isNew}
                        >
                          {accountTypes.map(mapAccountTypeToMenuItem)}
                        </SelectInput>
                      </Grid>
                      <Grid size={9}>
                        <TextFieldInput
                          fullWidth
                          data-testid="accountNumber"
                          disabled={!isNew}
                          label={t('admin.accountNumbers.accountNumber')}
                          name="id"
                          type="text"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {showProducts && (
                    <Grid size={12} sx={{ pb: 2 }}>
                      <Stack gap={1}>
                        <H6>{t('admin.accountNumbers.selectProducts')}</H6>
                        <ProductDataTable
                          isCCCOrg={isCCCOrg}
                          columns={['selectAll', 'selectProduct', 'selectReturnProduct']}
                          products={allAccountProducts}
                          selectedProducts={selectedProducts}
                          onChangeSelectedProducts={(newSelectedProducts) => {
                            setFieldValue('selectedProducts', newSelectedProducts)
                          }}
                        />
                        <ErrorMessage name="selectedProducts" component={FormError} />
                      </Stack>
                    </Grid>
                  )}

                  {showShipmentOptions && (
                    <Grid size={12}>
                      <H6>{t('admin.accountNumbers.selectServiceOptions')}</H6>
                      <Box
                        sx={{
                          columnCount: 3,
                          paddingTop: 0.25,
                          paddingRight: 0,
                          paddingBottom: 0,
                          paddingLeft: 3,
                        }}
                      >
                        {sortBy(shipmentOptions, ({ key }) => t(`admin.accountNumbers.service_option.${key}`)).map(
                          ({ key }) => {
                            const isSelected = selectedShipmentOptions.includes(key)
                            return (
                              <Box key={key} sx={{ marginBottom: 1, width: '100%' }} ml={-3}>
                                <Checkbox
                                  onChange={() =>
                                    toggleSelectedItem(selectedShipmentOptions, key, 'selectedShipmentOptions')
                                  }
                                  checked={isSelected}
                                  label={t(`admin.accountNumbers.service_option.${key}`)}
                                />
                              </Box>
                            )
                          }
                        )}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <Divider variant="fullWidth" />
              <DialogActions>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button data-testid="click-cancel" color="primary" onClick={onCancel}>
                    {t('admin.accountNumbers.cancel')}
                  </Button>
                  <Button
                    size="medium"
                    disabled={isSubmitting}
                    color="primary"
                    onClick={() => handleSubmit()}
                    variant="contained"
                  >
                    {isNew ? t('admin.accountNumbers.addAccountNumber') : t('admin.accountNumbers.updateAccountNumber')}
                  </Button>
                </Box>
              </DialogActions>
            </Dialog>
          </Form>
        )
      }}
    </Formik>
  )
}
